import useTranslation from 'next-translate/useTranslation';
import type {
  ContestSettings,
  PayoutSchedule,
  SlateLabelEnum,
  ContestType,
  ResizingMode,
} from '@betterpool/api-types/contests-service';
import { createStyles, useMantineTheme } from '@mantine/core';
import { useState } from 'react';

import { Text } from '~/domains/design-system/Text';
import { UnstyledButton } from '~/domains/design-system/Button';

import { Section, SectionContent } from '../components';

import PrizesSegmentsHeader from './PrizesSegmentsHeader';
import PrizesTable from './PrizeTable';
import PrizesSegmentsTitle from './PrizesSegmentsTitle';
import PrizesModal from './PrizesModal';
import { PrizesResizingMode, PrizesResizingInfo } from './PrizesResizingMode';

type PrizesProps = {
  prizes: PayoutSchedule[];
  isSidePanel?: boolean;
  resizingMode?: ResizingMode;
  contestType?: ContestType;
  segments?: ContestSettings['segments'];
  slateLabel?: SlateLabelEnum;
  contestTags?: string[];
};

const useStyles = createStyles((theme) => ({
  view: {
    width: '100%',
    textAlign: 'center',
    marginTop: theme.other.spacing._8,
  },
}));

function Prizes({
  prizes = [],
  resizingMode,
  isSidePanel,
  segments,
  contestType,
  contestTags,
  slateLabel,
}: PrizesProps) {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const { t } = useTranslation('contest');
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!prizes || prizes.length === 0) return null;

  return (
    <>
      <Section>
        <PrizesSegmentsTitle
          segments={segments}
          slateLabel={slateLabel}
          isSidePanel={isSidePanel}
          resizingMode={resizingMode}
        />
        <SectionContent isSidePanel={isSidePanel}>
          <PrizesTable slateLabel={slateLabel} prizes={prizes} segments={segments} />
          <PrizesSegmentsHeader segments={segments} slateLabel={slateLabel} />
          <PrizesResizingMode resizingMode={resizingMode} />
          <PrizesResizingInfo
            resizingMode={resizingMode}
            segments={segments}
            slateLabel={slateLabel}
          />
          <UnstyledButton className={classes.view} onClick={() => setIsModalOpen(true)}>
            <Text variant="label-small" c={theme.colors.blue[9]}>
              {t('detail.prizes.action.view')}
            </Text>
          </UnstyledButton>
        </SectionContent>
      </Section>
      <PrizesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contestType={contestType}
        contestTags={contestTags}
        segments={segments}
        slateLabel={slateLabel}
        prizes={prizes}
        resizingMode={resizingMode}
      />
    </>
  );
}

export default Prizes;

export type { PrizesProps };
